import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyForm = {
  name: "", category_id: "", sku: "", strength: "", description: "", research_info: "",
  price: "", unit_cost: "", stock_quantity: "", weight_oz: "1.5", featured: false, status: "active",
  images: [], seo_title: "", seo_description: "",
  coa: { batch_number: "", purity_percentage: "", test_date: "", manufacturing_date: "", expiration_date: "" },
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("/categories").then((res) => setCategories(res.data));
    if (isEdit) {
      apiClient.get("/admin/products").then((res) => {
        const p = res.data.items.find((x) => x.id === id);
        if (p) setForm({ ...p, price: String(p.price), unit_cost: p.unit_cost != null ? String(p.unit_cost) : "", stock_quantity: String(p.stock_quantity), coa: { ...p.coa, purity_percentage: String(p.coa.purity_percentage) } });
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        unit_cost: form.unit_cost === "" ? null : parseFloat(form.unit_cost),
        stock_quantity: parseInt(form.stock_quantity, 10),
        weight_oz: parseFloat(form.weight_oz) || 1.5,
        coa: { ...form.coa, purity_percentage: parseFloat(form.coa.purity_percentage), hplc_verified: true, mass_spec_verified: true, testing_lab: form.coa.testing_lab || "Independent Third-Party Laboratory" },
      };
      if (isEdit) await apiClient.put(`/admin/products/${id}`, payload);
      else await apiClient.post("/admin/products", payload);
      toast.success(`Product ${isEdit ? "updated" : "created"}`);
      navigate("/admin/products");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-product-form-page">
      <h1 className="text-2xl font-display font-semibold text-slate-900 mb-6">{isEdit ? "Edit Product" : "New Product"}</h1>
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" /></div>
          <div><Label>SKU</Label><Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-form-sku" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Strength</Label><Input required placeholder="e.g. 5mg/vial" value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} data-testid="product-form-strength" /></div>
          <div><Label>Price ($)</Label><Input type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" /></div>
          <div><Label>Unit Cost ($, admin only)</Label><Input type="number" min="0" step="0.01" placeholder="Your cost per unit" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="product-form-unit-cost" /></div>
          <div><Label>Stock Quantity</Label><Input type="number" required value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} data-testid="product-form-stock" /></div>
          <div><Label>Weight per Vial (oz)</Label><Input type="number" min="0" step="0.1" value={form.weight_oz} onChange={(e) => setForm({ ...form, weight_oz: e.target.value })} data-testid="product-form-weight" /></div>
        </div>

        <div><Label>Description</Label><Textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-form-description" /></div>
        <div><Label>Research Information</Label><Textarea rows={3} value={form.research_info} onChange={(e) => setForm({ ...form, research_info: e.target.value })} data-testid="product-form-research-info" /></div>
        <div><Label>Product Image URL</Label><Input placeholder="https://... (leave blank to use default imagery)" value={form.images?.[0] || ""} onChange={(e) => setForm({ ...form, images: e.target.value ? [e.target.value] : [] })} data-testid="product-form-image-url" /></div>

        <div className="grid sm:grid-cols-1 gap-4 pt-2 border-t border-slate-100">
          <div><Label>SEO Title</Label><Input placeholder="e.g. NAD+ 500mg | Research Use Only | True Impact Peptides" value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} data-testid="product-form-seo-title" /></div>
          <div><Label>SEO Meta Description</Label><Textarea rows={2} value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} data-testid="product-form-seo-description" /></div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
          <div><Label>Batch Number</Label><Input required value={form.coa.batch_number} onChange={(e) => setForm({ ...form, coa: { ...form.coa, batch_number: e.target.value } })} data-testid="product-form-batch" /></div>
          <div><Label>Purity (%)</Label><Input type="number" step="0.1" required value={form.coa.purity_percentage} onChange={(e) => setForm({ ...form, coa: { ...form.coa, purity_percentage: e.target.value } })} data-testid="product-form-purity" /></div>
          <div><Label>Test Date</Label><Input type="date" value={form.coa.test_date} onChange={(e) => setForm({ ...form, coa: { ...form.coa, test_date: e.target.value } })} data-testid="product-form-test-date" /></div>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="product-form-featured" /> Featured</label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="w-40" data-testid="product-form-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
          </Select>
        </div>

        {error && <p className="text-sm text-red-500" data-testid="product-form-error">{error}</p>}
        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={loading} className="rounded-md" data-testid="product-form-submit">{loading ? "Saving..." : "Save Product"}</Button>
          <Button type="button" variant="outline" className="rounded-md" onClick={() => navigate("/admin/products")}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
